import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Loading } from "../components/Loader";
import { Select } from "../components/Select";
import { apiFetch } from "../shared/api";
import { fetchStores, selectStores } from "../features/stores/storesSlice";
import {
  cancelStockAdjustment,
  clearDetail,
  deleteStockAdjustment,
  fetchStockAdjustmentDetail,
  postStockAdjustment,
  saveStockAdjustment,
  selectStockAdjustmentDetail,
  selectStockAdjustmentDetailError,
  selectStockAdjustmentDetailStatus,
} from "../features/stockAdjustments/stockAdjustmentsSlice";
import { REASON_OPTIONS, STATUS_LABELS, describeError, formatDateTime, statusColor } from "./stockAdjustmentShared";
import "./StockAdjustmentPages.css";

let rowSeq = 0;
const nextKey = () => {
  rowSeq += 1;
  return `row-${rowSeq}`;
};

function toInt(value) {
  if (value === "" || value === "-" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isInteger(n) ? n : null;
}

/** Поиск товара по названию или штрих-коду. */
function ProductPicker({ onPick, disabled }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await apiFetch(`/api/product/products/?search=${encodeURIComponent(q)}&page_size=12`, {
          method: "GET",
        });
        if (!cancelled) setResults(Array.isArray(data) ? data : data?.results || []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  return (
    <div className="picker">
      <input
        className="search"
        value={query}
        disabled={disabled}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Товар: название или штрих-код (от 2 символов)"
      />
      {(loading || results.length > 0) && (
        <div className="picker-results">
          {loading && <div className="picker-item muted">Поиск…</div>}
          {results.map((p) => (
            <button
              type="button"
              key={p.id}
              className="picker-item"
              onClick={() => {
                onPick(p);
                setQuery("");
                setResults([]);
              }}
            >
              <span>{p.name}</span>
              <span className="mono muted">{p.barcode}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function StockAdjustmentPage() {
  const { adjustmentId } = useParams();
  const isNew = !adjustmentId || adjustmentId === "new";
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const detail = useSelector(selectStockAdjustmentDetail);
  const detailStatus = useSelector(selectStockAdjustmentDetailStatus);
  const detailError = useSelector(selectStockAdjustmentDetailError);
  const stores = useSelector(selectStores);

  const [reason, setReason] = useState("other");
  const [comment, setComment] = useState("");
  const [rows, setRows] = useState([]);
  const [storeId, setStoreId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    dispatch(fetchStores({ pageSize: 200 }));
  }, [dispatch]);

  useEffect(() => {
    if (isNew) {
      dispatch(clearDetail());
      setRows([]);
      setReason("other");
      setComment("");
      return undefined;
    }
    dispatch(fetchStockAdjustmentDetail({ adjustmentId }));
    return () => dispatch(clearDetail());
  }, [adjustmentId, dispatch, isNew]);

  // Подтягиваем форму из загруженного документа.
  useEffect(() => {
    if (isNew || !detail || String(detail.id) !== String(adjustmentId)) return;
    setReason(detail.reason);
    setComment(detail.comment || "");
    setRows(
      (detail.items || []).map((it) => ({
        key: nextKey(),
        store: it.store,
        storeName: it.store_name,
        product: it.product,
        productName: it.product_name,
        barcode: it.product_barcode,
        before: it.quantity_before,
        newQty: String(it.new_quantity),
        delta: String(it.delta),
        mode: "new_quantity",
        current: it.current_quantity,
        appliedDelta: it.applied_delta,
        after: it.quantity_after,
      }))
    );
  }, [adjustmentId, detail, isNew]);

  const editable = isNew || detail?.status === "draft";
  const storeOptions = useMemo(() => stores.map((s) => ({ value: s.id, label: s.name })), [stores]);
  const storeName = useCallback((id) => stores.find((s) => s.id === id)?.name || `Магазин #${id}`, [stores]);

  const addProduct = useCallback(
    async (product) => {
      if (!storeId) {
        setMessage({ type: "error", text: "Сначала выберите магазин." });
        return;
      }
      if (rows.some((r) => r.store === storeId && r.product === product.id)) {
        setMessage({ type: "error", text: "Этот товар уже есть в документе для выбранного магазина." });
        return;
      }
      setMessage(null);
      let before = 0;
      try {
        const data = await apiFetch(`/api/product/inventory/?store=${storeId}&product=${product.id}`, { method: "GET" });
        const list = Array.isArray(data) ? data : data?.results || [];
        before = Number(list[0]?.quantity ?? 0);
      } catch {
        before = 0;
      }
      setRows((prev) => [
        ...prev,
        {
          key: nextKey(),
          store: storeId,
          storeName: storeName(storeId),
          product: product.id,
          productName: product.name,
          barcode: product.barcode,
          before,
          newQty: "",
          delta: "",
          mode: "new_quantity",
          current: before,
        },
      ]);
    },
    [rows, storeId, storeName]
  );

  const updateRow = (key, patch) => setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const onNewQty = (row, value) => {
    const n = toInt(value);
    updateRow(row.key, { newQty: value, delta: n === null ? "" : String(n - row.before), mode: "new_quantity" });
  };
  const onDelta = (row, value) => {
    const n = toInt(value);
    updateRow(row.key, { delta: value, newQty: n === null ? "" : String(row.before + n), mode: "delta" });
  };

  const buildItems = () => {
    const items = [];
    for (const r of rows) {
      const value = toInt(r.mode === "delta" ? r.delta : r.newQty);
      if (value === null) {
        throw new Error(`Укажите количество: ${r.productName} (${r.storeName}).`);
      }
      items.push({ store: r.store, product: r.product, [r.mode]: value });
    }
    if (!items.length) throw new Error("Добавьте хотя бы одну позицию.");
    return items;
  };

  const run = async (action) => {
    setBusy(true);
    setMessage(null);
    try {
      return await action();
    } catch (err) {
      setMessage({ type: "error", text: err?.message || describeError(err) });
      return null;
    } finally {
      setBusy(false);
    }
  };

  const save = () =>
    run(async () => {
      const result = await dispatch(
        saveStockAdjustment({ adjustmentId: isNew ? null : adjustmentId, reason, comment, items: buildItems() })
      );
      if (saveStockAdjustment.rejected.match(result)) {
        throw new Error(describeError(result.payload));
      }
      if (isNew) navigate(`/warehouse/adjustments/${result.payload.id}`, { replace: true });
      else setMessage({ type: "ok", text: "Черновик сохранён." });
      return result.payload;
    });

  const post = () =>
    run(async () => {
      // Проводим то, что на экране: сначала сохраняем черновик, затем проводим.
      const saved = await dispatch(
        saveStockAdjustment({ adjustmentId: isNew ? null : adjustmentId, reason, comment, items: buildItems() })
      );
      if (saveStockAdjustment.rejected.match(saved)) throw new Error(describeError(saved.payload));
      const id = saved.payload.id;
      const posted = await dispatch(postStockAdjustment({ adjustmentId: id }));
      if (postStockAdjustment.rejected.match(posted)) throw new Error(describeError(posted.payload));
      setMessage({ type: "ok", text: "Документ проведён, остатки обновлены." });
      if (isNew) navigate(`/warehouse/adjustments/${id}`, { replace: true });
    });

  const cancel = () =>
    run(async () => {
      const result = await dispatch(cancelStockAdjustment({ adjustmentId }));
      if (cancelStockAdjustment.rejected.match(result)) throw new Error(describeError(result.payload));
    });

  const remove = () =>
    run(async () => {
      if (!window.confirm("Удалить черновик?")) return;
      const result = await dispatch(deleteStockAdjustment({ adjustmentId }));
      if (deleteStockAdjustment.rejected.match(result)) throw new Error(describeError(result.payload));
      navigate("/warehouse/adjustments");
    });

  if (!isNew && detailStatus === "loading" && !detail) {
    return (
      <div className="stock-adjustments-page">
        <Loading />
      </div>
    );
  }
  if (!isNew && detailStatus === "failed") {
    return (
      <div className="stock-adjustments-page">
        <div className="error">{detailError}</div>
      </div>
    );
  }

  const status = detail?.status || "draft";

  return (
    <div className="stock-adjustments-page">
      <div className="header">
        <div>
          <div className="title">
            {isNew ? "Новая корректировка остатков" : `Корректировка остатков №${adjustmentId}`}
            {!isNew && (
              <span className="badge" style={{ borderColor: statusColor(status), color: statusColor(status) }}>
                {STATUS_LABELS[status] || status}
              </span>
            )}
          </div>
          {!isNew && (
            <div className="sub-title">
              Создано {formatDateTime(detail?.created_at)}
              {detail?.posted_at ? ` · проведено ${formatDateTime(detail.posted_at)}` : ""}
            </div>
          )}
        </div>
        <button type="button" className="btn" onClick={() => navigate("/warehouse/adjustments")}>
          К списку
        </button>
      </div>

      {message && <div className={message.type === "ok" ? "notice" : "error"}>{message.text}</div>}

      <div className="form">
        <div className="form-row">
          <label className="field">
            <span className="label">Причина</span>
            <Select options={REASON_OPTIONS} value={reason} onChange={(v) => setReason(v || "other")} isDisabled={!editable} isSearchable={false} />
          </label>
          <label className="field grow">
            <span className="label">Комментарий</span>
            <input className="search" value={comment} onChange={(e) => setComment(e.target.value)} disabled={!editable} />
          </label>
        </div>

        {editable && (
          <div className="form-row">
            <label className="field">
              <span className="label">Магазин для новой позиции</span>
              <Select options={storeOptions} value={storeId} onChange={setStoreId} placeholder="Выберите магазин…" />
            </label>
            <div className="field grow">
              <span className="label">Добавить товар</span>
              <ProductPicker onPick={addProduct} disabled={!storeId} />
            </div>
          </div>
        )}
      </div>

      <div className="body">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th className="th">Магазин</th>
                <th className="th">Товар</th>
                <th className="th num">В системе</th>
                <th className="th num">Новое количество</th>
                <th className="th num">Разница</th>
                {!editable && <th className="th num">Применено</th>}
                {!editable && <th className="th num">Стало</th>}
                {editable && <th className="th" />}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td className="td-muted" colSpan={8}>
                    Позиций нет. Выберите магазин и найдите товар.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.key} className="tr static">
                    <td className="td">{r.storeName}</td>
                    <td className="td">
                      {r.productName}
                      <div className="mono muted">{r.barcode}</div>
                    </td>
                    <td className="td num">
                      {r.before}
                      {editable && r.current !== null && r.current !== undefined && r.current !== r.before && (
                        <div className="muted" title="Остаток изменился после ввода позиции: разница применится к нему">
                          сейчас {r.current}
                        </div>
                      )}
                    </td>
                    <td className="td num">
                      {editable ? (
                        <input
                          className="qty"
                          inputMode="numeric"
                          value={r.newQty}
                          onChange={(e) => onNewQty(r, e.target.value)}
                        />
                      ) : (
                        r.newQty
                      )}
                    </td>
                    <td className="td num">
                      {editable ? (
                        <input
                          className="qty"
                          inputMode="numeric"
                          value={r.delta}
                          onChange={(e) => onDelta(r, e.target.value)}
                        />
                      ) : (
                        r.delta
                      )}
                    </td>
                    {!editable && <td className="td num">{r.appliedDelta ?? "—"}</td>}
                    {!editable && <td className="td num">{r.after ?? "—"}</td>}
                    {editable && (
                      <td className="td">
                        <button
                          type="button"
                          className="btn-ghost"
                          onClick={() => setRows((prev) => prev.filter((x) => x.key !== r.key))}
                        >
                          Убрать
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editable && (
        <div className="actions">
          <button type="button" className="btn-primary" disabled={busy} onClick={post}>
            Провести
          </button>
          <button type="button" className="btn" disabled={busy} onClick={save}>
            Сохранить черновик
          </button>
          {!isNew && (
            <>
              <button type="button" className="btn-ghost" disabled={busy} onClick={cancel}>
                Отменить документ
              </button>
              <button type="button" className="btn-ghost" disabled={busy} onClick={remove}>
                Удалить черновик
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
